import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SearchIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { useDebouncedCallback } from '@noor/lib';
import { Input } from '../input';
import { FilterClearButton } from './filter-clear-button';
import { useFiltersContext } from './filters-context';

const DEFAULT_DEBOUNCE_MS = 300;

export interface FiltersSearchProps {
  /** Search-param key this input writes to. */
  paramKey: string;
  placeholder?: string;
  'aria-label'?: string;
  debounceMs?: number;
  /** Leading icon (defaults to a lucide search glyph). */
  icon?: ReactNode;
  /** Class for the wrapper (controls width, etc.). */
  className?: string;
  /** Class for the `Input` itself (padding, radius, surface…). */
  inputClassName?: string;
}

/**
 * Debounced search input bound to a URL search param. Keeps a local draft so
 * typing stays responsive, and re-seeds from the URL when it changes
 * *externally* (back/forward, reset) — but never remounts on its own commit,
 * so the input keeps focus while you type.
 */
export function FiltersSearch({
  paramKey,
  placeholder = 'Search…',
  debounceMs = DEFAULT_DEBOUNCE_MS,
  icon,
  className,
  inputClassName,
  ...aria
}: FiltersSearchProps) {
  const { getParam, setParam } = useFiltersContext();
  const urlValue = getParam(paramKey) ?? '';
  const [draft, setDraft] = useState(urlValue);

  // Remember the last value we wrote to the URL so we can tell our own commit
  // apart from an external URL change (back/forward, a Reset button, etc.).
  const lastCommittedRef = useRef(urlValue);

  const commit = useDebouncedCallback((value: string) => {
    const next = value.trim();
    // Record our own write *before* it lands so the re-seed effect ignores it.
    lastCommittedRef.current = next;
    setParam(paramKey, next === '' ? undefined : next);
  }, debounceMs);

  // Re-seed the draft only when the URL changed for a reason *other* than our
  // own debounced commit. This keeps the input focused while typing (no key
  // remount) yet still reflects external navigation.
  useEffect(() => {
    if (urlValue === lastCommittedRef.current) return;
    lastCommittedRef.current = urlValue;
    setDraft(urlValue);
  }, [urlValue]);

  const ariaLabel = aria['aria-label'] ?? placeholder;

  const clear = (): void => {
    setDraft('');
    lastCommittedRef.current = '';
    commit.cancel();
    setParam(paramKey, undefined);
  };

  return (
    <div className={cn('relative w-full sm:w-[251px]', className)}>
      <span className="pointer-events-none absolute top-1/2 left-4 flex -translate-y-1/2 items-center text-muted-foreground">
        {icon ?? <SearchIcon className="size-5" />}
      </span>
      <Input
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          commit(e.target.value);
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        // Figma filter search: h-10 white pill on rounded-3xl, 14px text
        // text. `text-ellipsis` keeps a term longer than the box from
        // rendering flush against (and under) the clear button while
        // unfocused: the overflow truncates instead of butting into the icon.
        // The padding reserves the clear button's column so the text never
        // sits beneath it.
        className={cn(
          'h-10 rounded-[24px] bg-filter-bg pl-11 font-normal text-ellipsis placeholder:font-normal',
          draft ? 'pr-11' : undefined,
          inputClassName,
        )}
      />
      {draft ? (
        <FilterClearButton
          onClear={clear}
          aria-label={`${ariaLabel} — clear`}
          className="absolute top-1/2 right-4 -translate-y-1/2"
        />
      ) : null}
    </div>
  );
}
